import type { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiCustomerPortalContract } from '@montugroup/circuit-api-contracts';
import type { LatLng } from '@montugroup/design-system';
import { useMapCamera } from '@montugroup/design-system/MapView';
import type { ClientInferResponseBody } from '@ts-rest/core';
import { useEffect, useMemo } from 'react';

type ClickAndCollectOption = ClientInferResponseBody<
  typeof apiCustomerPortalContract.listCartDeliveryOptionsAndReserveInventory,
  typeof HttpStatus.OK
>['data']['ClickAndCollect']['0'];

type Props = {
  mapId: string;
  clickAndCollectOptions: ClickAndCollectOption[];
  searchedLocation: LatLng | null;
  selectedPharmacy: ClickAndCollectOption | null;
  confirmedPharmacy: ClickAndCollectOption | null;
};

const clickAndCollectOptionToCoordinates = ({ pharmacyLocation }: ClickAndCollectOption) => ({
  lat: pharmacyLocation.coordinates.latitude,
  lng: pharmacyLocation.coordinates.longitude,
});

export function useClickAndCollectCamera({
  mapId,
  clickAndCollectOptions,
  searchedLocation,
  selectedPharmacy,
  confirmedPharmacy,
}: Props) {
  const pointsInView: LatLng[] = useMemo(() => {
    return clickAndCollectOptions.map((option) => clickAndCollectOptionToCoordinates(option));
  }, [clickAndCollectOptions]);

  const { bounds, setFocalPoint } = useMapCamera({
    mapId,
    pointsInView,
  });

  const handleHomeClick = () => {
    setFocalPoint(searchedLocation);
  };

  // NOTE: This will update the focalPoint every list-delivery-options network request as it changes the selectedPharmacy & confirmedPharmacy objects
  // it seems like a "feature" ATM but consider moving the setFocalPoint to the user action itself rather than the useEffect dependancy trigger if not preferred
  useEffect(() => {
    if (confirmedPharmacy) {
      setFocalPoint(clickAndCollectOptionToCoordinates(confirmedPharmacy));
    } else if (selectedPharmacy) {
      setFocalPoint(clickAndCollectOptionToCoordinates(selectedPharmacy));
    } else {
      setFocalPoint(null);
    }
  }, [confirmedPharmacy, selectedPharmacy, setFocalPoint]);

  const initialCamera = bounds
    ? ({
        type: 'bounds',
        bounds,
      } as const)
    : undefined;

  return {
    initialCamera,
    handleHomeClick,
  };
}
